import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  bookingColumns,
  customerColumns,
  enquiryColumns,
  exportFileName,
  invoiceColumns,
  serviceColumns,
  toCsvFile,
  type ExportAssignmentRow,
  type ExportCustomerRow,
  type ExportDataset,
  type ExportEnquiryRow,
  type ExportInvoiceRow,
  type ExportJobRow,
  type ExportLookups,
  type ExportServiceRow,
} from '@/lib/dataExport';
import { buildZip } from '@/lib/zip';
import { CSV_BOM } from '@/lib/csv';

// New tables/RPCs aren't in the generated types yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

/** PostgREST returns at most 1,000 rows per request, so page through. */
export const EXPORT_PAGE_SIZE = 1000;

/**
 * Fetch every row of an org-scoped table the signed-in user can see (RLS
 * decides), a page at a time, in a stable order.
 */
async function fetchAll<T>(table: string, columns: string, orgId: string, orderBy = 'created_at'): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += EXPORT_PAGE_SIZE) {
    const { data, error } = await db
      .from(table)
      .select(columns)
      .eq('organization_id', orgId)
      .order(orderBy, { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + EXPORT_PAGE_SIZE - 1);
    if (error) throw error;
    const page = (data ?? []) as T[];
    rows.push(...page);
    if (page.length < EXPORT_PAGE_SIZE) break;
  }
  return rows;
}

async function fetchServices(orgId: string): Promise<ExportServiceRow[]> {
  const { data, error } = await db.rpc('export_org_services', { _org_id: orgId });
  if (error) throw error;
  return (data ?? []) as ExportServiceRow[];
}

async function fetchTeamNames(orgId: string): Promise<Map<string, string>> {
  const rows = await fetchAll<{ id: string; name: string | null; email: string | null }>(
    'team_members',
    'id, name, email',
    orgId,
  );
  return new Map(rows.map((r) => [r.id, r.name?.trim() || r.email || 'Team member']));
}

const JOB_COLUMNS =
  'id, title, status, scheduled_date, start_time, end_time, client_name, client_email, client_phone, address, service_id, team_member_id, customer_id, required_cleaners, customer_notes, internal_notes, notes, created_at';
const ENQUIRY_COLUMNS =
  'id, created_at, status, subject, client_name, client_email, client_phone, address, service_id, frequency, premises_size, additional_info, customer_id, converted_booking_id';
const INVOICE_COLUMNS =
  'id, invoice_number, status, issued_at, due_date, customer_name, customer_email, customer_id, booking_id, currency_code, subtotal_amount, tax_amount, total_amount, deposit_amount, amount_paid, amount_refunded, notes, created_at';

/** Build one dataset's CSV (with BOM) for an org. */
export async function buildDatasetCsv(orgId: string, dataset: ExportDataset): Promise<string> {
  switch (dataset) {
    case 'customers':
      return toCsvFile(
        await fetchAll<ExportCustomerRow>('customers', 'id, name, email, phone, address, created_at', orgId),
        customerColumns,
      );
    case 'services':
      return toCsvFile(await fetchServices(orgId), serviceColumns);
    case 'invoices':
      return toCsvFile(await fetchAll<ExportInvoiceRow>('invoices', INVOICE_COLUMNS, orgId), invoiceColumns);
    case 'enquiries': {
      const [rows, services] = await Promise.all([
        fetchAll<ExportEnquiryRow>('enquiries', ENQUIRY_COLUMNS, orgId),
        fetchServices(orgId),
      ]);
      const serviceNames = new Map(services.map((s) => [s.id, s.name ?? '']));
      return toCsvFile(rows, enquiryColumns({ serviceNames }));
    }
    case 'bookings': {
      const [jobs, assignmentRows, services, teamNames] = await Promise.all([
        fetchAll<ExportJobRow>('jobs', JOB_COLUMNS, orgId, 'scheduled_date'),
        fetchAll<ExportAssignmentRow & { id: string }>('job_assignments', 'id, job_id, team_member_id, created_at', orgId),
        fetchServices(orgId),
        fetchTeamNames(orgId),
      ]);
      const assignments = new Map<string, string[]>();
      for (const a of assignmentRows) {
        assignments.set(a.job_id, [...(assignments.get(a.job_id) ?? []), a.team_member_id]);
      }
      const lookups: ExportLookups = {
        serviceNames: new Map(services.map((s) => [s.id, s.name ?? ''])),
        teamNames,
        assignments,
      };
      return toCsvFile(jobs, bookingColumns(lookups));
    }
  }
}

/** Trigger a browser download of a Blob. */
export function downloadBlob(fileName: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export interface ExportInput {
  orgId: string;
  orgName: string | null | undefined;
  /** One dataset, or 'all' for a ZIP of every dataset the user may export. */
  dataset: ExportDataset | 'all';
  /** Datasets to include in the 'all' ZIP. */
  include?: ExportDataset[];
}

export const ALL_DATASETS: ExportDataset[] = ['customers', 'bookings', 'enquiries', 'invoices', 'services'];

/** Download one CSV, or every CSV in a ZIP ("Download everything"). */
export function useDataExport() {
  return useMutation({
    mutationFn: async (input: ExportInput): Promise<{ fileName: string }> => {
      if (input.dataset !== 'all') {
        const csv = await buildDatasetCsv(input.orgId, input.dataset);
        const fileName = exportFileName(input.dataset, input.orgName);
        downloadBlob(fileName, new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        return { fileName };
      }
      const datasets = input.include ?? ALL_DATASETS;
      // One at a time keeps big orgs from firing dozens of parallel page requests.
      const entries = [];
      for (const dataset of datasets) {
        entries.push({ name: `${dataset}.csv`, content: await buildDatasetCsv(input.orgId, dataset) });
      }
      entries.push({
        name: 'README.txt',
        content:
          `${CSV_BOM}Your data from The Polished Version${input.orgName ? ` for ${input.orgName}` : ''}.\r\n` +
          `Exported ${new Date().toISOString()}.\r\n\r\n` +
          'Each CSV opens in Excel, Numbers or Google Sheets. Dates are YYYY-MM-DD and times are UK time.\r\n' +
          'Cells that start with = + - or @ have an apostrophe added so spreadsheets treat them as text.\r\n',
      });
      const zip = buildZip(entries);
      const fileName = exportFileName('all-data', input.orgName, 'zip');
      downloadBlob(fileName, new Blob([zip], { type: 'application/zip' }));
      return { fileName };
    },
  });
}

// ---------------------------------------------------------------------------
// Business deletion
// ---------------------------------------------------------------------------

export interface OrgDeletionRequest {
  organization_id: string;
  requested_at: string;
  purge_after: string;
}

/** The org's pending deletion, if the owner has asked for it to be deleted. */
export function useOrgDeletionRequest(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['org-deletion-request', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<OrgDeletionRequest | null> => {
      const { data, error } = await db
        .from('organization_deletion_requests')
        .select('organization_id, requested_at, purge_after')
        .eq('organization_id', orgId)
        .maybeSingle();
      if (error) throw error;
      const row = data as Partial<OrgDeletionRequest> | null;
      return row?.purge_after ? (row as OrgDeletionRequest) : null;
    },
  });
}

export interface DeleteOrgResult {
  ok: boolean;
  purgeAfter: string | null;
  stripeCanceled?: boolean;
  stripeSkipped?: boolean;
}

/** Pull the `{ error }` message out of a non-2xx edge function response. */
async function functionErrorMessage(error: unknown): Promise<string> {
  const context = (error as { context?: Response })?.context;
  if (context && typeof context.json === 'function') {
    try {
      const body = await context.clone().json();
      if (body?.error) return String(body.error);
    } catch {
      // fall through
    }
  }
  return (error as Error)?.message || 'Something went wrong. Please try again.';
}

/** Ask the delete-organization edge function to delete the business. */
export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { organizationId: string; confirmName: string }): Promise<DeleteOrgResult> => {
      const { data, error } = await supabase.functions.invoke('delete-organization', { body: input });
      if (error) throw new Error(await functionErrorMessage(error));
      return (data ?? { ok: false, purgeAfter: null }) as DeleteOrgResult;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['org-deletion-request', input.organizationId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Customer data requests (right of access / erasure)
// ---------------------------------------------------------------------------

export interface CustomerIdentity {
  customerId: string | null;
  email: string | null;
  phone: string | null;
}

const digits = (value: string | null | undefined) => (value ?? '').replace(/\D/g, '');

/** PostgREST `or` filter matching a customer's id, email or phone on a table. */
function identityFilter(identity: CustomerIdentity, cols: { id?: string; email?: string }): string | null {
  const parts: string[] = [];
  if (identity.customerId && cols.id) parts.push(`${cols.id}.eq.${identity.customerId}`);
  const email = identity.email?.trim();
  if (email && cols.email && !/[,()"]/.test(email)) parts.push(`${cols.email}.ilike.${email.replace(/[%_]/g, '\\$&')}`);
  return parts.length ? parts.join(',') : null;
}

async function fetchMatching<T>(
  table: string,
  columns: string,
  orgId: string,
  identity: CustomerIdentity,
  cols: { id?: string; email?: string; phone?: string },
): Promise<T[]> {
  const filter = identityFilter(identity, cols);
  const results: T[] = [];
  if (filter) {
    const { data, error } = await db.from(table).select(columns).eq('organization_id', orgId).or(filter);
    if (error) throw error;
    results.push(...((data ?? []) as T[]));
  }
  // Phone numbers are stored in many formats, so match those on digits client-side.
  const phone = digits(identity.phone);
  if (cols.phone && phone.length >= 6) {
    const { data, error } = await db
      .from(table)
      .select(columns)
      .eq('organization_id', orgId)
      .not(cols.phone, 'is', null)
      // Narrow on the last 3 digits (usually written together), then compare all digits.
      .ilike(cols.phone, `%${phone.slice(-3)}%`)
      .limit(5000);
    if (error) throw error;
    const seen = new Set(results.map((r) => (r as { id: string }).id));
    for (const row of (data ?? []) as Array<T & { id: string }>) {
      const value = (row as Record<string, unknown>)[cols.phone] as string | null;
      if (!seen.has(row.id) && digits(value) === phone) results.push(row);
    }
  }
  return results;
}

/** Everything held about one customer, as a JSON document (right of access). */
export async function buildCustomerExport(orgId: string, orgName: string | null | undefined, identity: CustomerIdentity) {
  const [customers, bookings, enquiries, invoices, threads] = await Promise.all([
    fetchMatching<{ id: string }>('customers', 'id, name, email, phone, address, created_at, updated_at', orgId, identity, {
      id: 'id',
      email: 'email',
      phone: 'phone',
    }),
    fetchMatching<{ id: string }>('jobs', `${JOB_COLUMNS}, updated_at`, orgId, identity, {
      id: 'customer_id',
      email: 'client_email',
      phone: 'client_phone',
    }),
    fetchMatching<{ id: string }>('enquiries', `${ENQUIRY_COLUMNS}, updated_at`, orgId, identity, {
      id: 'customer_id',
      email: 'client_email',
      phone: 'client_phone',
    }),
    fetchMatching<{ id: string }>('invoices', INVOICE_COLUMNS, orgId, identity, {
      id: 'customer_id',
      email: 'customer_email',
    }),
    identity.customerId
      ? fetchMatching<{ id: string }>('customer_message_threads', 'id, subject, enquiry_id, booking_id, created_at', orgId, identity, {
          id: 'customer_id',
        })
      : Promise.resolve([] as { id: string }[]),
  ]);

  let messages: unknown[] = [];
  if (threads.length > 0) {
    const { data, error } = await db
      .from('customer_messages')
      .select('id, thread_id, sender_type, body, created_at')
      .eq('organization_id', orgId)
      .in('thread_id', threads.map((t) => t.id))
      .order('created_at', { ascending: true });
    if (error) throw error;
    messages = data ?? [];
  }

  let lineItems: unknown[] = [];
  if (invoices.length > 0) {
    const { data, error } = await db
      .from('invoice_line_items')
      .select('invoice_id, description, quantity, unit_amount, line_total, sort_order')
      .eq('organization_id', orgId)
      .in('invoice_id', invoices.map((i) => i.id))
      .order('sort_order', { ascending: true });
    if (error) throw error;
    lineItems = data ?? [];
  }

  return {
    exported_at: new Date().toISOString(),
    business: orgName ?? null,
    note: 'Personal data held about this customer, provided under UK GDPR (right of access).',
    customer_records: customers,
    bookings,
    enquiries,
    invoices,
    invoice_line_items: lineItems,
    message_threads: threads,
    messages,
  };
}

/** Download one customer's data as JSON. */
export function useCustomerDataExport() {
  return useMutation({
    mutationFn: async (input: { orgId: string; orgName: string | null | undefined; identity: CustomerIdentity; label: string }) => {
      const doc = await buildCustomerExport(input.orgId, input.orgName, input.identity);
      const fileName = exportFileName(`customer-${input.label}`, input.orgName, 'json');
      downloadBlob(fileName, new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' }));
      return { fileName };
    },
  });
}

export interface EraseCustomerResult {
  jobs: number;
  invoices: number;
  enquiries: number;
  callbacks: number;
  threads: number;
  customers: number;
}

/** Erase/anonymise one customer (owner only; enforced by the RPC). */
export function useEraseCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { orgId: string; identity: CustomerIdentity }): Promise<EraseCustomerResult> => {
      const { data, error } = await db.rpc('erase_customer_data', {
        _org_id: input.orgId,
        _customer_id: input.identity.customerId,
        _email: input.identity.email,
        _phone: input.identity.phone,
      });
      if (error) throw error;
      return data as EraseCustomerResult;
    },
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: ['customers-admin', input.orgId] });
    },
  });
}
